import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../constants/AppTable';
import { LoadingSpinner } from '../../../constants/AppLoading';
import DialogBox from '../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../constants/InputTextBox';
import { toleValidationSchema } from '../../../helpers/validation';
import { useFormik } from 'formik';
import NepaliInputBox from '../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../helpers/handleChange';
import { EndBaseLine } from '../../../constants/AppSpace';
import { Button } from '../../../constants/Button';
import { putDataToTheServer } from '../../../helpers/api';
import AppURL from '../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../constants/Toast';

interface MargaTole {
    sn: number;
    tole_name: string;
    tole_name_nep: string;
}

interface MargaToleListViewProps {
    data: {
        results: MargaTole[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const MargaToleListView: React.FC<MargaToleListViewProps> = ({ data, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<MargaTole | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Marga Tole', accessor: 'tole_name', sortable: false },
        { header: 'Marga Tole(Nepali)', accessor: 'tole_name_nep', sortable: true },
        { header: 'Actions', accessor: 'actions', action: true },
    ];

    const modifiedData = useMemo(() => {
        if (!data) return [];
        return data.results.map((item: MargaTole) => ({
            sn: item.sn,
            tole_name: item.tole_name,
            tole_name_nep: item.tole_name_nep,
            actions: (
                <button onClick={() => handleEdit(item)}>Edit</button>
            ),
        }));
    }, [data]);

    const handleEdit = (item: MargaTole) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };

    // Initialize Formik before the conditional rendering
    const formik = useFormik({
        initialValues: {
            tole_name: currentItem?.tole_name || "",
            tole_name_nep: currentItem?.tole_name_nep || "",
        },
        validationSchema: toleValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("tole_name", values.tole_name);
            formData.append("tole_name_nep", values.tole_name_nep);
            try {
                await putDataToTheServer(AppURL.FrontOfficeURL + 'front/marga/tole/retrieve/update/view/'+currentItem?.sn, formData).then((res) => {
                    if(res?.status===200){
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    }else if(res?.status===400){
                        ToastWarning({ message: res?.message?.tole_name });
                    }
                    else{
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => data?.next && setOnURL(data.next)}
                onPrev={() => data?.previous && setOnURL(data.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Tole Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='tole_name'
                                    label='Marga/Tole Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.tole_name}
                                    error={typeof formik.errors.tole_name === 'string' ? formik.errors.tole_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.tole_name}
                                    required={true}
                                    placeholder='Enter Marga/Tole Name'
                                />
                            </Grid>

                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Marga/Tole Name(Nepali)'
                                    name='tole_name_nep'
                                    value={formik.values.tole_name_nep}
                                    handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'tole_name_nep' })}
                                    required={true}
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.tole_name_nep === 'string' ? formik.errors.tole_name_nep : undefined}
                                    placeholder='Enter Marga/Tole nepali name'
                                    touched={formik.touched.tole_name_nep}
                                />
                            </Grid>
                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='save' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default MargaToleListView;
